import { TableInputContainer } from "../../../components/tableInputContainer/TableInputContainer";
import { DataButton, InputCheckbox, InputNumber } from "@sellar-io/components";
import {
  AvailabilityTierListResponseType,
  DeliveryAreaListResponseType,
  CustomerZoneListResponseType,
  OrderRulesListResponseType,
  PriceListListResponseType,
} from "@sellar-io/api-v2/types";
import {
  AcquiredViaFilterType,
  AcquiredViaFilterValues,
  CustomerDeliveryFilterType,
  CustomerDeliveryFilterValues,
  CustomerPaymentFilterType,
  CustomerPaymentFilterValues,
  DateUnits,
} from "./CustomerListFilterTypes";
import { twMerge } from "tailwind-merge";
import { InputSelect, InputOption } from "@sellar-io/components";
import { capitalize } from "@sellar-io/common";
import { useState } from "react";
import { useCustomerListStore } from "./CustomerListStore";
import { useShallow } from "zustand/react/shallow";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";

export type CustomerListFilterValues = {
  priceTier: string;
  deliveryTier: CustomerDeliveryFilterType;
  paymentTerms: CustomerPaymentFilterType;
  avgOrderMin: string;
  avgOrderMax: string;
  lastOrderedCount: string;
  lastOrderedUnit: DateUnits;
  lastOrderedMoreThan: boolean;
  hasDiscounts: boolean;
  acquiredVia: AcquiredViaFilterType;
};

export const CustomerListFilter = ({
  deliveryAreas,
  customerZones,
  priceTiers,
  availabilityTiers,
  orderRules,
  onDismiss,
  onApplyFilter,
  isMobile,
  filtersApplied,
}: {
  deliveryAreas: DeliveryAreaListResponseType;
  customerZones: CustomerZoneListResponseType;
  priceTiers: PriceListListResponseType;
  availabilityTiers: AvailabilityTierListResponseType;
  orderRules: OrderRulesListResponseType;
  onDismiss: () => void;
  onApplyFilter: () => void;
  isMobile?: boolean;
  filtersApplied: boolean;
}) => {
  const unbundling2aEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2A",
  );

  const unbundling2bEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2B",
  );

  const unbundlingEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );

  const sellarDistributionEnabled = useRemoteFeatureFlags().isActive(
    "sellarDistributionBeta",
  );

  const state = useCustomerListStore(useShallow((state) => state));

  const dateUnits: DateUnits[] = ["days", "weeks", "months", "years"];
  const [paymentTerms, setPaymentTerms] = useState(state.paymentTerms);
  const [priceTier, setPriceTier] = useState(state.priceTier);
  const [availabilityTier, setAvailabilityTier] = useState(
    state.availabilityTier,
  );
  const [orderRule, setOrderRule] = useState(state.orderRule);
  const [customerZoneId, setCustomerZoneId] = useState(state.customerZoneId);
  const [
    insideSellarDistributionDeliveryArea,
    setInsideSellarDistributionDeliveryArea,
  ] = useState(state.insideSellarDistributionDeliveryArea);
  const [deliveryAreaId, setDeliveryAreaId] = useState(state.deliveryAreaId);
  const [deliveryTier, setDeliveryTier] = useState(state.deliveryTier);
  const [avgOrderMin, setAvgOrderMin] = useState(state.avgOrderMin);
  const [avgOrderMax, setAvgOrderMax] = useState(state.avgOrderMax);
  const [lastOrderedCount, setLastOrderedCount] = useState(
    state.lastOrderedCount,
  );
  const [lastOrderedUnit, setLastOrderedUnit] = useState(state.lastOrderedUnit);
  const [lastOrderedMoreThan, setLastOrderedMoreThan] = useState(
    state.lastOrderedMoreThan,
  );
  const [hasDiscounts, setHasDiscounts] = useState(state.hasDiscounts);
  const [acquiredVia, setAcquiredVia] = useState(state.acquiredVia);

  const disabled =
    paymentTerms === state.paymentTerms &&
    priceTier === state.priceTier &&
    availabilityTier === state.availabilityTier &&
    orderRule === state.orderRule &&
    (!unbundlingEnabled || customerZoneId === state.customerZoneId) &&
    insideSellarDistributionDeliveryArea ===
      state.insideSellarDistributionDeliveryArea &&
    deliveryAreaId === state.deliveryAreaId &&
    deliveryTier === state.deliveryTier &&
    avgOrderMin === state.avgOrderMin &&
    avgOrderMax === state.avgOrderMax &&
    lastOrderedCount === state.lastOrderedCount &&
    lastOrderedUnit === state.lastOrderedUnit &&
    lastOrderedMoreThan === state.lastOrderedMoreThan &&
    hasDiscounts === state.hasDiscounts &&
    acquiredVia === state.acquiredVia;

  const onApply = (reset: boolean) => {
    if (reset) {
      useCustomerListStore.getState().reset();
    } else {
      useCustomerListStore.getState().setState({
        ...state,
        paymentTerms,
        priceTier,
        availabilityTier,
        orderRule,
        customerZoneId,
        insideSellarDistributionDeliveryArea,
        page: 1,
        deliveryAreaId,
        deliveryTier,
        avgOrderMin,
        avgOrderMax,
        lastOrderedCount,
        lastOrderedUnit,
        lastOrderedMoreThan,
        hasDiscounts: !unbundlingEnabled && hasDiscounts,
        acquiredVia,
      });
    }
    onApplyFilter();

    onDismiss();
  };

  return (
    <>
      <div
        className={twMerge(
          "w-full",
          isMobile ? "px-4 pt-2" : "border-b border-zinc-300 p-4",
        )}
      >
        <span className="font-semibold">Filter customers</span>
      </div>
      <div className="w-full p-4">
        <div className="flex flex-col gap-3">
          <div className="flex w-full items-start justify-between">
            <span className="mt-2">
              {unbundling2bEnabled ? "Price list" : "Price tier"}
            </span>
            <div className="ml-4 flex w-[55%] flex-col gap-2">
              <TableInputContainer className="w-full">
                <InputSelect
                  defaultValue={priceTier}
                  onChange={(e) => setPriceTier(e.target.value)}
                  className="text-sm font-medium"
                >
                  <InputOption value={""}>
                    {unbundling2bEnabled
                      ? "All price lists"
                      : "All price tiers"}
                  </InputOption>
                  {priceTiers
                    .filter((pt) => pt.derivationType !== "base")
                    .map((priceTier) => (
                      <InputOption key={priceTier.id} value={priceTier.id}>
                        {priceTier.name}
                      </InputOption>
                    ))}
                </InputSelect>
              </TableInputContainer>
              {!unbundlingEnabled && (
                <label className="flex cursor-pointer items-center text-sm font-medium">
                  <InputCheckbox
                    checked={hasDiscounts}
                    onChange={(e) => setHasDiscounts(e.target.checked)}
                    className="mr-2"
                  />
                  Discounts assigned
                </label>
              )}
            </div>
          </div>

          {unbundlingEnabled ? (
            <>
              <div className="flex w-full items-start justify-between">
                <span className="mt-2">Delivery zone</span>
                <div className="ml-4 flex w-[55%] flex-col gap-2">
                  <TableInputContainer className="w-full">
                    <InputSelect
                      aria-label="Delivery zone"
                      value={customerZoneId}
                      onChange={(e) => setCustomerZoneId(e.target.value)}
                      className="text-sm font-medium"
                    >
                      <InputOption value="">All delivery zones</InputOption>
                      {customerZones
                        .filter(
                          (zone) =>
                            zone.systemDefinedType !== "sellarDistribution",
                        )
                        .map((zone) => (
                          <InputOption key={zone.id} value={zone.id}>
                            {zone.name ??
                              (zone.default
                                ? "Nationwide"
                                : `Delivery zone ${zone.id}`)}
                          </InputOption>
                        ))}
                    </InputSelect>
                  </TableInputContainer>
                </div>
              </div>
              <div className="flex w-full items-center justify-between">
                <span>Default delivery method</span>
                <TableInputContainer className="ml-4 w-[55%]">
                  <InputSelect
                    defaultValue={deliveryTier}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDeliveryTier(value as CustomerDeliveryFilterType);
                    }}
                    className="text-sm font-medium"
                  >
                    {Object.values(CustomerDeliveryFilterValues)
                      .filter(
                        (delivery) =>
                          sellarDistributionEnabled ||
                          delivery.key !== "sellarDistribution",
                      )
                      .map((value) => (
                        <InputOption key={value.key} value={value.key}>
                          {value.key === "all" && unbundlingEnabled
                            ? "All delivery methods"
                            : value.name}
                        </InputOption>
                      ))}
                  </InputSelect>
                </TableInputContainer>
              </div>
              <div className="flex w-full items-center justify-between">
                <span>Delivery schedule</span>
                <TableInputContainer className="ml-4 w-[55%]">
                  <InputSelect
                    defaultValue={deliveryAreaId}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDeliveryAreaId(value);
                    }}
                    className="text-sm font-medium"
                  >
                    <InputOption value="">All schedules</InputOption>
                    {deliveryAreas.map((deliveryArea) => (
                      <InputOption
                        key={deliveryArea.id}
                        value={deliveryArea.id}
                      >
                        {deliveryArea.name}
                      </InputOption>
                    ))}
                  </InputSelect>
                </TableInputContainer>
              </div>
            </>
          ) : (
            <div className="flex w-full items-center justify-between">
              <span>Delivery tier</span>
              <TableInputContainer className="ml-4 w-[55%]">
                <InputSelect
                  defaultValue={deliveryTier}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (value.includes("%%DA")) {
                      value = value.split("%%")[0] ?? "";
                      setDeliveryAreaId(value);
                    } else {
                      setDeliveryTier(value as CustomerDeliveryFilterType);
                    }
                  }}
                  className="text-sm font-medium"
                >
                  {Object.values(CustomerDeliveryFilterValues)
                    .filter(
                      (delivery) =>
                        sellarDistributionEnabled ||
                        delivery.key !== "sellarDistribution",
                    )
                    .map((value) => (
                      <InputOption key={value.key} value={value.key}>
                        {value.name}
                      </InputOption>
                    ))}
                  {deliveryAreas.length > 0 && (
                    <InputOption disabled>-----</InputOption>
                  )}
                  {deliveryAreas.map((deliveryArea) => (
                    <InputOption
                      key={deliveryArea.id}
                      value={`${deliveryArea.id}%%DA`}
                    >
                      {deliveryArea.name}
                    </InputOption>
                  ))}
                </InputSelect>
              </TableInputContainer>
            </div>
          )}

          <div className="ml-auto w-[55%]">
            <label className="flex cursor-pointer items-center text-sm font-medium">
              <InputCheckbox
                checked={insideSellarDistributionDeliveryArea}
                onChange={(e) =>
                  setInsideSellarDistributionDeliveryArea(e.target.checked)
                }
                className="mr-2"
              />
              Inside Sellar Distribution delivery area
            </label>
          </div>

          <div className="flex w-full items-start justify-between">
            <span className="mt-2">Product catalogue</span>
            <div className="ml-4 flex w-[55%] flex-col gap-2">
              <TableInputContainer className="w-full">
                <InputSelect
                  defaultValue={availabilityTier}
                  onChange={(e) => setAvailabilityTier(e.target.value)}
                  className="text-sm font-medium"
                >
                  <InputOption value={""}>All product catalogues</InputOption>
                  {availabilityTiers.map((availabilityTier) => (
                    <InputOption
                      key={availabilityTier.id}
                      value={availabilityTier.id}
                    >
                      {availabilityTier.name}
                    </InputOption>
                  ))}
                </InputSelect>
              </TableInputContainer>
            </div>
          </div>

          {unbundling2aEnabled && !unbundlingEnabled && (
            <div className="flex w-full items-start justify-between">
              <span className="mt-2">Order rules</span>
              <div className="ml-4 flex w-[55%] flex-col gap-2">
                <TableInputContainer className="w-full">
                  <InputSelect
                    defaultValue={orderRule}
                    onChange={(e) => setOrderRule(e.target.value)}
                    className="text-sm font-medium"
                  >
                    <InputOption value={""}>All order rules</InputOption>
                    {orderRules.map((orderRule) => (
                      <InputOption key={orderRule.id} value={orderRule.id}>
                        {orderRule.name}
                      </InputOption>
                    ))}
                  </InputSelect>
                </TableInputContainer>
              </div>
            </div>
          )}

          <div className="flex w-full items-start justify-between">
            <span className="mt-2">Last ordered</span>
            <div className="ml-4 flex w-[55%] flex-col flex-wrap gap-2">
              <div className="h-[36px]">
                <TableInputContainer className="gap-0 p-0 text-sm">
                  <span
                    className={twMerge(
                      "w-1/2 cursor-pointer rounded-l-lg border-r border-zinc-200 py-2 pl-3 text-sm font-medium",
                      lastOrderedMoreThan && "bg-zinc-100",
                    )}
                    onClick={() => setLastOrderedMoreThan(true)}
                  >
                    More than
                  </span>
                  <span
                    className={twMerge(
                      "w-1/2 cursor-pointer rounded-r-lg py-2 pr-3 pl-2 text-sm font-medium",
                      !lastOrderedMoreThan && "bg-zinc-100",
                    )}
                    onClick={() => setLastOrderedMoreThan(false)}
                  >
                    Less than
                  </span>
                </TableInputContainer>
              </div>
              <div className="flex items-center pr-4">
                <TableInputContainer className="w-[75px]">
                  <InputNumber
                    value={lastOrderedCount}
                    onChange={(e) => setLastOrderedCount(e.target.value)}
                    className="h-[19px] [appearance:textfield] text-sm font-medium [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                </TableInputContainer>
                <TableInputContainer className="ml-2 w-full">
                  <InputSelect
                    defaultValue={lastOrderedUnit}
                    onChange={(e) =>
                      setLastOrderedUnit(
                        e.target.value as typeof lastOrderedUnit,
                      )
                    }
                    className="text-sm font-medium"
                  >
                    {dateUnits.map((value) => (
                      <InputOption key={value} value={value}>
                        {capitalize(value)}
                      </InputOption>
                    ))}
                  </InputSelect>
                </TableInputContainer>
                <span className="ml-2 inline-block text-sm font-medium">
                  ago
                </span>
              </div>
            </div>
          </div>

          <div className="flex w-full items-center justify-between">
            <span>Payment terms</span>
            <TableInputContainer className="ml-4 w-[55%]">
              <InputSelect
                defaultValue={paymentTerms}
                onChange={(e) =>
                  setPaymentTerms(e.target.value as CustomerPaymentFilterType)
                }
                className="text-sm font-medium"
              >
                {Object.values(CustomerPaymentFilterValues).map((value) => (
                  <InputOption key={value.key} value={value.key}>
                    {value.name}
                  </InputOption>
                ))}
              </InputSelect>
            </TableInputContainer>
          </div>
          <div className="flex w-full items-start justify-between">
            <span className="mt-2">Avg. order value</span>
            <div className="ml-4 flex w-[55%] flex-col gap-2">
              <div className="flex items-center">
                <span className="mr-2 inline-block w-[60px] text-sm font-medium">
                  Min.
                </span>
                <TableInputContainer className="w-full">
                  <span className="leading-none font-medium text-zinc-400">
                    £
                  </span>
                  <InputNumber
                    defaultValue={avgOrderMin}
                    min="0"
                    onChange={(e) => setAvgOrderMin(e.target.value)}
                    className="h-[19px] text-sm font-medium"
                  />
                </TableInputContainer>
              </div>
              <div className="flex items-center">
                <span className="mr-2 inline-block w-[60px] text-sm font-medium">
                  Max.
                </span>
                <TableInputContainer className="w-full">
                  <span className="leading-none font-medium text-zinc-400">
                    £
                  </span>
                  <InputNumber
                    defaultValue={avgOrderMax}
                    min={avgOrderMin}
                    onChange={(e) => {
                      const maxVal = parseInt(e.target.value);
                      if (maxVal <= parseInt(avgOrderMin)) return;
                      setAvgOrderMax(e.target.value);
                    }}
                    className="h-[19px] text-sm font-medium"
                  />
                </TableInputContainer>
              </div>
            </div>
          </div>
          <div className="flex w-full items-center justify-between">
            <span>Acquired via</span>
            <TableInputContainer className="ml-4 w-[55%]">
              <InputSelect
                defaultValue={acquiredVia}
                onChange={(e) =>
                  setAcquiredVia(e.target.value as AcquiredViaFilterType)
                }
                className="text-sm font-medium"
              >
                {Object.values(AcquiredViaFilterValues).map((value) => (
                  <InputOption key={value.key} value={value.key}>
                    {value.name}
                  </InputOption>
                ))}
              </InputSelect>
            </TableInputContainer>
          </div>
        </div>
      </div>
      <div className="flex w-full items-center p-4">
        <DataButton
          onClick={() => onApply(false)}
          border={true}
          disabled={disabled}
        >
          {filtersApplied ? "Update" : "Apply"}
        </DataButton>
        {filtersApplied && (
          <DataButton color="transparent" onClick={() => onApply(true)}>
            Clear all
          </DataButton>
        )}
      </div>
    </>
  );
};
